import * as R from "./Rect2D.js";
import * as S from "./Shape2D.js";
import * as V from "./Vector2.js";
export function constructBVHTree(leafNodes, start, end) {
    if (start === end)
        return leafNodes[start];
    const mid = Math.floor((start + end) / 2);
    const left = constructBVHTree(leafNodes, start, mid);
    const right = constructBVHTree(leafNodes, mid + 1, end);
    return {
        value: null,
        aabb: R.bounds(left.aabb, right.aabb),
        mortonCode: -1,
        isLeaf: false,
        left,
        right,
    };
}
// The quickest way to construct and query the BVH is to sort the array of rects in-place.
// It also seems to speed up time to check intersections
export function fromShapes(shapes) {
    if (shapes.length === 0) {
        return {
            value: S.fromValues(),
            aabb: R.fromValues(),
            mortonCode: -1,
            isLeaf: true,
            left: null,
            right: null,
        };
    }
    const leafNodes = shapes.map((shape) => {
        const aabb = S.boundingBox(shape);
        return {
            value: shape,
            aabb,
            mortonCode: V.mortonCode(R.center(aabb)),
            isLeaf: true,
            left: null,
            right: null,
        };
    });
    // Rectangles sorted in order of their morton codes.
    leafNodes.sort((a, b) => a.mortonCode - b.mortonCode);
    return constructBVHTree(leafNodes, 0, leafNodes.length - 1);
}
export function fromElements(elements) {
    if (elements.length === 0) {
        return {
            value: new Element(),
            aabb: R.fromValues(),
            mortonCode: -1,
            isLeaf: true,
            left: null,
            right: null,
        };
    }
    const leafNodes = elements.map((el) => {
        const aabb = el.getBoundingClientRect();
        return {
            value: el,
            aabb,
            mortonCode: V.mortonCode(R.center(aabb)),
            isLeaf: true,
            left: null,
            right: null,
        };
    });
    // Rectangles sorted in order of their morton codes.
    leafNodes.sort((a, b) => a.mortonCode - b.mortonCode);
    return constructBVHTree(leafNodes, 0, leafNodes.length - 1);
}
export function intersections(root, rect) {
    const stack = [root];
    let node;
    const collisions = [];
    while ((node = stack.pop())) {
        if (!R.intersects(rect, node.aabb))
            continue;
        if (node.isLeaf) {
            collisions.push(node.value);
        }
        else {
            // push right node before left node
            stack.push(node.right, node.left);
        }
    }
    return collisions;
}
export function breathFirstTraverse(root, cb) {
    const queue = [root];
    let node;
    while ((node = queue.shift())) {
        if (cb(node) === false)
            continue;
        if (!node.isLeaf)
            queue.push(node.right, node.left);
    }
}
export function depthFirstTraverse(root, cb) {
    const stack = [root];
    let node;
    while ((node = stack.pop())) {
        if (cb(node) === false)
            continue;
        if (!node.isLeaf)
            stack.push(node.right, node.left);
    }
}
export function proximal(root, rect, proximity) {
    return intersections(root, R.expand(rect, proximity));
}
export function nearestShape(root, shape, direction) {
    const stack = [root];
    let node;
    const bounds = S.boundingBox(shape);
    const center = R.center(bounds);
    let distance = Infinity;
    let closestShape;
    while ((node = stack.pop())) {
        const nodeRect = node.aabb;
        if (nodeRect.x + nodeRect.width <= center.x)
            continue;
        if (node.isLeaf) {
            if (node.value === shape)
                continue;
            const nearestPoint = R.nearestPointOnRect(nodeRect, center);
            const d = V.distance(center, nearestPoint);
            if (d >= 0 && d < distance) {
                distance = d;
                closestShape = node.value;
            }
        }
        else {
            stack.push(node.right, node.left);
        }
    }
    return closestShape;
}
//# sourceMappingURL=BoundingVolumeHierarchy.js.map